import { useState, useMemo } from "react";
import { Helmet } from "react-helmet";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate, Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Search, Calendar, User, ArrowRight } from "lucide-react";
import { format } from "date-fns";
import { Navbar } from "@/components/layout/Navbar";

// Blog Schema for SEO
const blogSchema = {
  "@context": "https://schema.org",
  "@type": "Blog",
  "@id": "https://spotlightadz.ma/blog#blog",
  "name": "SpotlightAdz Blog - Digital Marketing Insights",
  "description": "Expert digital marketing insights, SEO tips, social media strategies, and business growth tactics from SpotlightAdz, Morocco's leading marketing agency.",
  "url": "https://spotlightadz.ma/blog",
  "publisher": {
    "@type": "Organization",
    "@id": "https://spotlightadz.ma/#organization",
    "name": "SpotlightAdz",
    "logo": {
      "@type": "ImageObject",
      "url": "https://spotlightadz.ma/logo.png",
      "width": 512,
      "height": 512
    }
  },
  "inLanguage": ["en", "fr", "ar"],
  "isPartOf": {
    "@type": "WebSite",
    "@id": "https://spotlightadz.ma/#website"
  }
};

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    {
      "@type": "ListItem",
      "position": 1,
      "name": "Home",
      "item": "https://spotlightadz.ma"
    },
    {
      "@type": "ListItem",
      "position": 2,
      "name": "Blog",
      "item": "https://spotlightadz.ma/blog"
    }
  ]
};

const Blog = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const { data: posts, isLoading } = useQuery({
    queryKey: ["blog-posts", searchTerm, selectedCategory],
    queryFn: async () => {
      let query = supabase
        .from("blog_posts")
        .select("*")
        .eq("status", "published")
        .order("published_at", { ascending: false });

      if (searchTerm) {
        query = query.or(`title.ilike.%${searchTerm}%,excerpt.ilike.%${searchTerm}%,content.ilike.%${searchTerm}%`);
      }

      if (selectedCategory) {
        query = query.eq("category", selectedCategory);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
  });

  const { data: categories } = useQuery({
    queryKey: ["blog-categories"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("blog_categories")
        .select("*")
        .order("name");
      if (error) throw error;
      return data;
    },
  });

  // Generate CollectionPage schema from posts
  const collectionSchema = useMemo(() => {
    if (!posts || posts.length === 0) return null;
    return {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "@id": "https://spotlightadz.ma/blog#collection",
      "name": "SpotlightAdz Blog Articles",
      "description": "Browse all digital marketing articles, SEO guides, and social media strategies from SpotlightAdz.",
      "url": "https://spotlightadz.ma/blog",
      "isPartOf": { "@id": "https://spotlightadz.ma/#website" },
      "mainEntity": {
        "@type": "ItemList",
        "itemListElement": posts.slice(0, 10).map((post, index) => ({
          "@type": "ListItem",
          "position": index + 1,
          "url": `https://spotlightadz.ma/blog/${post.slug}`,
          "name": post.title,
        })),
      },
    };
  }, [posts]);

  return (
    <div className="min-h-screen bg-background">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Digital Marketing Blog | SpotlightAdz - Morocco's Top Agency</title>
        <meta name="title" content="Digital Marketing Blog | SpotlightAdz - Morocco's Top Agency" />
        <meta name="description" content="Discover expert digital marketing insights, SEO tips, social media strategies, and business growth tactics from SpotlightAdz, Morocco's leading marketing agency. Visit spotlightadz.ma" />
        <meta name="keywords" content="digital marketing blog, SEO tips Morocco, social media marketing strategies, content marketing, SpotlightAdz blog, marketing insights, spotlightadz.ma, spotlightadz.com" />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <link rel="canonical" href="https://spotlightadz.ma/blog" />
        
        {/* Open Graph */}
        <meta property="og:type" content="blog" />
        <meta property="og:url" content="https://spotlightadz.ma/blog" />
        <meta property="og:title" content="Digital Marketing Blog | SpotlightAdz" />
        <meta property="og:description" content="Expert digital marketing insights from Morocco's top agency. SEO, social media, paid ads, and growth strategies." />
        <meta property="og:image" content="https://spotlightadz.ma/og-image.png" />
        <meta property="og:site_name" content="SpotlightAdz" />
        <meta property="og:locale" content="en_US" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Digital Marketing Blog | SpotlightAdz" />
        <meta name="twitter:description" content="Expert digital marketing insights from Morocco's top agency" />
        <meta name="twitter:image" content="https://spotlightadz.ma/og-image.png" />
        <meta name="twitter:site" content="@spotlightadz" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(blogSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
        {collectionSchema && (
          <script type="application/ld+json">
            {JSON.stringify(collectionSchema)}
          </script>
        )}
      </Helmet>
      
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative py-20 border-b border-border/50 bg-gradient-to-b from-primary/5 to-background">
        <div className="responsive-container">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
              SpotlightAdz Blog
            </h1>
            <p className="text-lg text-muted-foreground mb-4">
              Digital marketing insights, trends, and strategies to help your business grow
            </p>
            <p className="text-sm text-muted-foreground mb-8">
              Your trusted resource from <a href="https://spotlightadz.ma" className="text-primary hover:underline" rel="noopener">spotlightadz.ma</a> | <a href="https://spotlightadz.com" className="text-primary hover:underline" rel="noopener">spotlightadz.com</a>
            </p>
            
            {/* Search */}
            <div className="relative max-w-md mx-auto">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search articles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                aria-label="Search blog articles"
              />
            </div>
          </div>
        </div>
      </section>

      <div className="responsive-container py-12">
        <div className="grid lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <aside className="lg:col-span-1">
            <Card className="p-6 sticky top-4">
              <h3 className="font-semibold mb-4">Categories</h3>
              <div className="space-y-2">
                <Button
                  variant={selectedCategory === null ? "default" : "ghost"}
                  className="w-full justify-start"
                  onClick={() => setSelectedCategory(null)}
                >
                  All Posts
                </Button>
                {categories?.map((category) => (
                  <Button
                    key={category.id}
                    variant={selectedCategory === category.name ? "default" : "ghost"}
                    className="w-full justify-start"
                    onClick={() => setSelectedCategory(category.name)}
                  >
                    {category.name}
                  </Button>
                ))}
              </div>
            </Card>
          </aside>

          {/* Blog Posts */}
          <div className="lg:col-span-3">
            {isLoading ? (
              <div className="grid md:grid-cols-2 gap-6">
                {[1, 2, 3, 4].map((i) => (
                  <Card key={i} className="overflow-hidden">
                    <Skeleton className="h-48 w-full" />
                    <div className="p-6 space-y-4">
                      <Skeleton className="h-4 w-20" />
                      <Skeleton className="h-6 w-full" />
                      <Skeleton className="h-20 w-full" />
                    </div>
                  </Card>
                ))}
              </div>
            ) : posts && posts.length > 0 ? (
              <div className="grid md:grid-cols-2 gap-6" role="feed" aria-label="Blog articles">
                {posts.map((post) => (
                  <article
                    key={post.id}
                    className="overflow-hidden rounded-lg border bg-card text-card-foreground shadow-sm hover:shadow-lg transition-shadow cursor-pointer group"
                    onClick={() => navigate(`/blog/${post.slug}`)}
                    itemScope
                    itemType="https://schema.org/BlogPosting"
                  >
                    {post.featured_image && (
                      <div className="aspect-video overflow-hidden bg-muted">
                        <img
                          src={post.featured_image}
                          alt={post.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          loading="lazy"
                          width="600"
                          height="338"
                          itemProp="image"
                        />
                      </div>
                    )}
                    <div className="p-6">
                      <div className="flex items-center gap-2 mb-3">
                        <Badge variant="secondary" itemProp="articleSection">{post.category}</Badge>
                        <time 
                          className="text-sm text-muted-foreground flex items-center gap-1"
                          dateTime={post.published_at}
                          itemProp="datePublished"
                        >
                          <Calendar className="h-3 w-3" />
                          {format(new Date(post.published_at), "MMM dd, yyyy")}
                        </time>
                      </div>
                      
                      <h2 className="text-xl font-semibold mb-2 group-hover:text-primary transition-colors" itemProp="headline">
                        {post.title}
                      </h2>
                      
                      <p className="text-muted-foreground mb-4 line-clamp-3" itemProp="description">
                        {post.excerpt}
                      </p>
                      
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground" itemProp="author" itemScope itemType="https://schema.org/Person">
                          <User className="h-4 w-4" />
                          <span itemProp="name">{post.author_name}</span>
                        </div>
                        <Button variant="ghost" size="sm" className="group-hover:text-primary" aria-label={`Read more about ${post.title}`}>
                          Read More
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <meta itemProp="url" content={`https://spotlightadz.ma/blog/${post.slug}`} />
                    <span itemProp="publisher" itemScope itemType="https://schema.org/Organization" className="hidden">
                      <meta itemProp="name" content="SpotlightAdz" />
                    </span>
                  </article>
                ))}
              </div>
            ) : (
              <Card className="p-12 text-center">
                <p className="text-muted-foreground">No blog posts found</p>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Blog;
